type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const ACCESS_TOKEN = "access_token";
// Admin's own token, parked while they view the app as another user.
const ADMIN_TOKEN = "admin_access_token";
const IMPERSONATION = "impersonation";

export interface ImpersonationInfo {
  username: string;
  displayName?: string;
  expiresAt: string;
  readOnly: boolean;
}

// The dashboard JWT lasts 7 days. A tab left open past that keeps polling
// with the dead token and every request gets a 401, so the dashboard shows
// stale data and nothing the user does works. Send them back to /login,
// exactly like the Logout button does. During impersonation (30-min token)
// a 401 means the impersonation expired: restore the admin session instead.
export const isSessionExpired = (response: Pick<Response, "status">) =>
  response.status === 401;

export const endExpiredSession = (
  storage: StorageLike = window.localStorage,
  location: { href: string } = window.location,
) => {
  if (storage.getItem(ADMIN_TOKEN)) {
    exitImpersonation(storage, location);
    return;
  }
  storage.removeItem(ACCESS_TOKEN);
  location.href = "/login";
};

export const getImpersonation = (
  storage: Pick<Storage, "getItem"> = window.localStorage,
): ImpersonationInfo | null => {
  if (!storage.getItem(ADMIN_TOKEN)) return null;
  try {
    const raw = storage.getItem(IMPERSONATION);
    return raw ? (JSON.parse(raw) as ImpersonationInfo) : null;
  } catch {
    return null;
  }
};

export const startImpersonation = (
  impersonationToken: string,
  info: ImpersonationInfo,
  storage: StorageLike = window.localStorage,
  location: { href: string } = window.location,
) => {
  const adminToken = storage.getItem(ACCESS_TOKEN);
  // Never park an impersonation token as the "admin" token (nested sudo).
  if (!adminToken || storage.getItem(ADMIN_TOKEN)) return;
  storage.setItem(ADMIN_TOKEN, adminToken);
  storage.setItem(IMPERSONATION, JSON.stringify(info));
  storage.setItem(ACCESS_TOKEN, impersonationToken);
  location.href = "/dashboard/control";
};

/** Discard the impersonation token and restore the admin's own session. */
export const exitImpersonation = (
  storage: StorageLike = window.localStorage,
  location: { href: string } = window.location,
) => {
  const adminToken = storage.getItem(ADMIN_TOKEN);
  storage.removeItem(ADMIN_TOKEN);
  storage.removeItem(IMPERSONATION);
  if (adminToken) {
    storage.setItem(ACCESS_TOKEN, adminToken);
    location.href = "/dashboard/admin/users";
  } else {
    storage.removeItem(ACCESS_TOKEN);
    location.href = "/login";
  }
};

/** Logout: drop every token, including a parked admin token. */
export const clearSession = (storage: StorageLike = window.localStorage) => {
  storage.removeItem(ACCESS_TOKEN);
  storage.removeItem(ADMIN_TOKEN);
  storage.removeItem(IMPERSONATION);
};
